/**
 * MUSEFOLK Case Compositor v9
 * 修复并发洪泛：限制同时加载数≤3，加超时，优化重试
 */

const TEMPLATES = {
  '17pm': {
    src: (window.TPL_URLS||{})['17pm'] || 'template-17pm.webp',
    mask: (window.TPL_URLS||{})['17pm-mask'] || 'backpanel-17pm.png',
    w: 760, h: 1492,
    // Matches the 2px-expanded mask generated from the latest ProMax render.
    art: { x: 107, y: 436, w: 549, h: 866 }
  },
  '17pm-3view': {
    src: (window.TPL_URLS||{})['17pm-3view'] || 'template-17pm-3view.webp',
    mask: (window.TPL_URLS||{})['17pm-3view-mask'] || 'backpanel-17pm-3view.png',
    w: 1280, h: 1280,
    // Matches the 2px-expanded mask generated from the latest ProMax render.
    art: { x: 408, y: 417, w: 465, h: 733 }
  }
};

// 型号→模板映射（18pm/18p/17pm/17p 全部复用 17pm 模板）
const MODEL_TO_TPL = { '18pm':'17pm', '18p':'17pm', '17pm':'17pm', '17p':'17pm' };
function tplKey(model, view){ var base = MODEL_TO_TPL[model] || model; return (view==='3view') ? base+'-3view' : base; }

// === 并发限制器（最多3个图片同时下载，避免打爆浏览器6连接限制）===
const _queue = [];
let _active = 0;
const MAX_CONCURRENT = 6;

function _drain() {
  while (_active < MAX_CONCURRENT && _queue.length) {
    const task = _queue.shift();
    _active++;
    task().then(
      function(){ _active--; _drain(); },
      function(){ _active--; _drain(); }
    );
  }
}

const imgCache = {};
const _pending = {};  // 去重：同一URL只发一次请求，多个调用者共享Promise

function loadImg(src) {
  if (imgCache[src]) return Promise.resolve(imgCache[src]);
  if (_pending[src]) return _pending[src];  // 已有相同URL在排队/加载中，复用
  var _res, _rej;
  var p = new Promise(function(res, rej){ _res = res; _rej = rej; });
  _queue.push(function(){ return new Promise(function(res2, rej2){
    _doLoad(src, res2, rej2);
  }).then(function(img){ _res(img); }, function(err){ _rej(err); }); });
  _pending[src] = p;
  p.then(function(){ delete _pending[src]; }, function(){ delete _pending[src]; });
  _drain();
  return p;
}

function _doLoad(src, resolve, reject) {
  var attempts = 0;
  function tryLoad() {
    attempts++;
    var img = new Image();
    if (!location.protocol.startsWith('file')) img.crossOrigin = 'anonymous';
    var timer = setTimeout(function() {
      img.onload = img.onerror = null;
      img.src = '';
      if (attempts < 2) { tryLoad(); }
      else reject(new Error('Timeout: ' + src));
    }, 8000);
    img.onload = function(){ clearTimeout(timer); imgCache[src] = img; resolve(img); };
    img.onerror = function() {
      clearTimeout(timer);
      if (attempts < 2) { setTimeout(tryLoad, 500); }
      else reject(new Error('Failed: ' + src));
    };
    img.src = src;
  }
  tryLoad();
}

function coverFit(iW, iH, tW, tH) {
  const ir = iW / iH, tr = tW / tH;
  let sx, sy, sw, sh;
  if (ir > tr) { sh = iH; sw = iH * tr; sx = (iW - sw) / 2; sy = 0; }
  else { sw = iW; sh = iW / tr; sx = 0; sy = (iH - sh) / 2; }
  return { sx, sy, sw, sh };
}

// ss-06 has two lines of artwork that must remain visible.  The approved
// production rule is a 5% vertical squash followed by the source window used
// by the final static render.  Coordinates are kept at the 2880x5120 master
// resolution and scaled for the compressed artwork assets loaded by Shopify.
const SS06_SPECIAL = {
  referenceWidth: 2880,
  referenceHeight: 5120,
  squashY: 0.95,
  front: { top: 291, height: 4299 },
  three: { top: 296, height: 4289 }
};

function isSs06Artwork(url) {
  const s = String(url || '').toLowerCase().split(/[?#]/)[0];
  return /mf-ssebong-06(?:_4x)?\.(?:jpg|jpeg|png)$/.test(s)
    || /artists\/ssebong\/06(?:\.(?:jpg|jpeg|png))?$/.test(s);
}

function drawSs06Artwork(ctx, artwork, art, type) {
  const ref = type === '17pm-3view' ? SS06_SPECIAL.three : SS06_SPECIAL.front;
  const yScale = artwork.naturalHeight / SS06_SPECIAL.referenceHeight;
  const cropTop = ref.top * yScale;
  const cropHeight = ref.height * yScale;
  const squashedHeight = Math.max(1, Math.round(artwork.naturalHeight * SS06_SPECIAL.squashY));

  // Draw the source into a temporary canvas first so the vertical squash is
  // applied before extracting the approved content window.
  const squashed = document.createElement('canvas');
  squashed.width = artwork.naturalWidth;
  squashed.height = squashedHeight;
  const sctx = squashed.getContext('2d');
  sctx.drawImage(artwork, 0, 0, artwork.naturalWidth, squashedHeight);

  const scale = art.h / cropHeight;
  const drawW = artwork.naturalWidth * scale;
  const drawH = cropHeight * scale;
  const dx = art.x - (drawW - art.w) / 2;
  const dy = art.y;
  ctx.drawImage(squashed, 0, cropTop, artwork.naturalWidth, cropHeight, dx, dy, drawW, drawH);
}

async function composeCase(artworkUrl, type) {
  type = type || '17pm';
  const tpl = TEMPLATES[type];
  if(!tpl) throw new Error('Unknown template: '+type);
  const W = tpl.w, H = tpl.h;
  const art = tpl.art;

  const [artwork, templateImg, maskImg] = await Promise.all([
    loadImg(artworkUrl),
    loadImg(tpl.src),
    loadImg(tpl.mask)
  ]);

  // --- 临时canvas：裁剪画作到背板形状 ---
  const tmp = document.createElement('canvas');
  tmp.width = W;
  tmp.height = H;
  const tctx = tmp.getContext('2d');

  if (isSs06Artwork(artworkUrl)) {
    // Keep the title and signature inside the case for both front and 3-view
    // realtime composites.  Other products retain the normal cover-fit path.
    drawSs06Artwork(tctx, artwork, art, type);
  } else {
    // 画作 cover-fit 到放大1.1倍的 bbox（超出mask部分会被裁掉，消除白边）
    const BLEED = 1.1;
    const { sx, sy, sw, sh } = coverFit(artwork.naturalWidth, artwork.naturalHeight, art.w, art.h);
    const dw = art.w * BLEED, dh = art.h * BLEED;
    const dx = art.x - (dw - art.w) / 2, dy = art.y - (dh - art.h) / 2;
    tctx.drawImage(artwork, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  // 用 mask 裁剪（只在临时canvas上操作，不影响主画布）
  tctx.globalCompositeOperation = 'destination-in';
  tctx.drawImage(maskImg, 0, 0, W, H);

  // --- 主canvas：白底 → 裁剪后画作 → 模板 ---
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // 1. 白底
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);

  // 2. 画上裁剪后的画作（背板外是透明的，不会覆盖白底）
  ctx.drawImage(tmp, 0, 0);

  // 3. 叠加模板（壳外白底+边框+摄像头，盖住画作边缘）
  ctx.drawImage(templateImg, 0, 0, W, H);

  return canvas;
}

// 只画壳子（白背板+银框模板），不等画作——用于秒出占位图（模板已预加载时几乎瞬时）
async function composeShell(type) {
  type = type || '17pm';
  const tpl = TEMPLATES[type];
  if(!tpl) throw new Error('Unknown template: '+type);
  const templateImg = await loadImg(tpl.src);
  const canvas = document.createElement('canvas');
  canvas.width = tpl.w;
  canvas.height = tpl.h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, tpl.w, tpl.h);
  ctx.drawImage(templateImg, 0, 0, tpl.w, tpl.h);
  return canvas;
}

// 预加载模板+mask（页面加载时调用，让卡片合成时已缓存）
function prefetchCaseAssets(type) {
  type = type || '17pm';
  const tpl = TEMPLATES[type];
  if(!tpl) return;
  loadImg(tpl.src).catch(function(){});
  loadImg(tpl.mask).catch(function(){});
}
